<script setup lang="ts">
/**
 * MessageBubble — Pajamas-styled chat turn (system / user / assistant).
 *
 * The component only owns presentation; the composer, streaming machinery,
 * and conversation reset live one level up in `ChatView`. Markdown is
 * rendered via a tiny safe pipeline (`renderMarkdownSafely`) per the Story
 * 303 challenger note.
 */
import { computed } from 'vue';
import { renderMarkdownSafely } from '../services/markdown';

const props = defineProps<{
  role: 'system' | 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  inputTokens?: number;
  outputTokens?: number;
}>();

const html = computed(() => renderMarkdownSafely(props.content));
const labelByRole: Record<string, string> = {
  system: 'System',
  user: 'You',
  assistant: 'Claude',
};

async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.content);
  } catch {
    // Clipboard is best-effort.
  }
}
</script>

<template>
  <article :class="['cwc-bubble', `cwc-bubble--${role}`]" :aria-live="streaming ? 'polite' : undefined">
    <header class="cwc-bubble__header">
      <span class="cwc-bubble__role">{{ labelByRole[role] }}</span>
      <span v-if="streaming" class="cwc-bubble__status" data-testid="streaming-indicator">streaming...</span>
      <button
        v-if="role === 'assistant' && !streaming"
        type="button"
        class="cwc-bubble__copy"
        aria-label="Copy assistant message"
        data-testid="copy-button"
        @click="copy"
      >
        Copy
      </button>
    </header>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div class="cwc-bubble__body" v-html="html" />
    <footer
      v-if="role === 'assistant' && (typeof inputTokens === 'number' || typeof outputTokens === 'number')"
      class="cwc-bubble__footer"
    >
      <span v-if="typeof inputTokens === 'number'" data-testid="input-tokens">in: {{ inputTokens }}</span>
      <span v-if="typeof outputTokens === 'number'" data-testid="output-tokens">out: {{ outputTokens }}</span>
    </footer>
  </article>
</template>

<style scoped>
.cwc-bubble {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-2);
  padding: var(--gl-spacing-3) var(--gl-spacing-4);
  border-radius: var(--gl-border-radius-lg);
  background: var(--gl-bg-surface);
  border: var(--gl-border-width) solid var(--gl-border-default);
  max-width: min(720px, 100%);
}

.cwc-bubble--system { background: var(--gl-bg-surface-subtle); }
.cwc-bubble--user   { align-self: flex-end; background: var(--gl-status-info-bg); color: var(--gl-status-info-fg); }
.cwc-bubble--assistant { align-self: flex-start; }

.cwc-bubble__header {
  display: flex;
  align-items: center;
  gap: var(--gl-spacing-3);
  font-size: var(--gl-font-size-sm);
  color: var(--gl-fg-subtle);
}

.cwc-bubble__role {
  font-weight: var(--gl-font-weight-bold);
  color: var(--gl-fg-default);
}

.cwc-bubble__status {
  font-style: italic;
}

.cwc-bubble__copy {
  margin-left: auto;
  appearance: none;
  background: transparent;
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  color: var(--gl-fg-default);
  padding: 0 var(--gl-spacing-2);
  font: inherit;
  cursor: pointer;
}

.cwc-bubble__body :deep(p) { margin: 0 0 var(--gl-spacing-2); }
.cwc-bubble__body :deep(p:last-child) { margin-bottom: 0; }
.cwc-bubble__body :deep(pre) {
  padding: var(--gl-spacing-3);
  background: var(--gl-bg-surface-subtle);
  border-radius: var(--gl-border-radius-base);
  overflow-x: auto;
  font-family: var(--gl-font-family-mono);
  font-size: var(--gl-font-size-sm);
}
.cwc-bubble__body :deep(code) {
  font-family: var(--gl-font-family-mono);
  font-size: var(--gl-font-size-sm);
}

.cwc-bubble__footer {
  display: flex;
  gap: var(--gl-spacing-3);
  font-size: var(--gl-font-size-sm);
  color: var(--gl-fg-subtle);
  border-top: var(--gl-border-width) solid var(--gl-border-default);
  padding-top: var(--gl-spacing-2);
}
</style>
